import { Component, inject, OnInit, signal, effect } from '@angular/core';
import { DesignPresenceDto } from './design-presence.model';
import { DesignPresenceApiService } from './design-presence.service';
import { StorageKeys, SessionStorageKeys, StoreService } from '../../services/store.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { User } from '../user-admin-page/user-model';
import { CurrentAppIdsDto } from '../../models/current-app-ids-dto';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-design-presence-modal',
  standalone: false,
  template: `
    <p-dialog
      [visible]="showPresenceModal()"
      (visibleChange)="showPresenceModal.set($event)"
      [modal]="true"
      [closable]="true"
      [style]="{ width: '400px' }"
      [dismissableMask]="true"
      [baseZIndex]="20000"
      [autoZIndex]="false"
      styleClass="design-presence-dialog"
      maskStyleClass="design-presence-mask"
    >
      <ng-template #header>
        <i [style.color]="'orange'" [style.fontSize]="'2rem'" class="pi pi-exclamation-triangle"></i>
        <span class="text-xl">Users in This Design</span>
      </ng-template>
      <p>The following users have accessed this design in the last 14 hours. Check with them before making edits.</p>
      @if (otherUsers().length > 0) {
        <div>
          <ul class="list-disc pl-5 space-y-2 mt-2">
            <li *ngFor="let user of otherUsers()" >
              <i [style.color]="'orange'" class="pi pi-user mr-2"></i>
              {{ user.fullName }} <br />
              <span class="text-xs">
                Last seen: {{ getRecentTime(user.lastUpdated) }}
              </span>
            </li>
          </ul>
        </div>
      }
    </p-dialog>
  `
})
export class DesignPresenceModalComponent implements OnInit {
  // Signals
  showPresenceModal = signal<boolean>(false);
  otherUsers = signal<DesignPresenceDto[]>([]);
  currentUserId = signal<string>('');
  isInitialized = signal<boolean>(false);
  private lastCheckedDesignId = signal<string | undefined>(undefined);

  // Services
  private _designPresenceService = inject(DesignPresenceApiService);
  private _storeService = inject(StoreService);

  // Get the current design as a signal
  private appIds = toSignal(
    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS)
  );

  constructor() {
    effect(() => {
      const appIds = this.appIds();
      const currentDesignId = appIds?.designId;
      
      // Only check if initialized and designId has actually changed
      if (this.isInitialized() && currentDesignId && currentDesignId !== this.lastCheckedDesignId()) {
        console.log(`Checking DesignId:${currentDesignId} for other users`);
        this.lastCheckedDesignId.set(currentDesignId);
        this.checkDesignPresence();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const user = await this._storeService.get<User>(StorageKeys.USER);
    this.currentUserId.set(user.id);
    this.isInitialized.set(true);
  }

  private async checkDesignPresence(): Promise<void> {
    try {
      const users = await lastValueFrom(this._designPresenceService.getDesignPresence());

      if (!users) {
        this.otherUsers.set([]);
        return;
      }

      const filtered = users
        .filter(u => this.excludeCurrentUser(u))
        .filter(u => this.isRecentUser(u));
      
      this.otherUsers.set(filtered);

      if (filtered.length > 0) {
        this.showPresenceModal.set(true);
      }
    } catch (err) {
      console.error('Error checking design presence:', err);
    }
  }

  private excludeCurrentUser(user: DesignPresenceDto): boolean {
    return user.userId !== this.currentUserId();
  }

  private isRecentUser(user: DesignPresenceDto): boolean {
    const nowLocal = new Date();
    const lastUpdatedUtc = new Date(user.lastUpdated);
    const diffMs = nowLocal.getTime() - lastUpdatedUtc.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours < 12;
  }

  public getRecentTime(utc: string): string {
    const nowLocal = new Date(); // Get the current local time
    const lastUpdatedUtc = new Date(utc);

    const diffMs = nowLocal.getTime() - lastUpdatedUtc.getTime(); // Difference in milliseconds
    const diffMinutes = Math.round(diffMs / 60000); // Difference in minutes (rounded)
    const diffHours = Math.round(diffMs / (1000 * 60 * 60)); // Difference in hours (rounded)

    // Less than 60 minutes - show minutes ago
    if (Math.abs(diffMinutes) < 60) {
      return `(${Math.abs(diffMinutes)} min ago)`;
    }

    // Less than 24 hours - show hours ago
    if (Math.abs(diffHours) < 24) {
      return `(${Math.abs(diffHours)} hr ago)`;
    }

    // More than or equal to 24 hours - show date and time in local format
    return lastUpdatedUtc.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

}